// A client fork's OWN VIEW of a field on the work item screen — through the client config seam
// (client-custom/frontend/client.config.js), one entry per field:
//
//   export const FIELD_VIEWS = [
//     { field: 'GPS Location', label: 'Site scope', collapsible: true,
//       load: () => import('@client/customs/ags/scope/ScopeField.jsx') },
//   ];
//
// `field` is the field's name as the site names it (any case) or its id (customfield_10123). The view
// is loaded only when a work item shows that field (its own chunk), takes the place of the field's value
// on the screen and saves itself. It gets:
//   { issueKey, fieldId, fieldName, value, editable, onFieldsChanged(fields), onAttachmentAdded(att) }
// onFieldsChanged puts saved values on the screen at once; onAttachmentAdded shows a file it attached
// under Attachments at once.
// Optional: `label` — the words over it on the work item screen (the field keeps its name everywhere
// else: ZQL, search, other screens); `collapsible: true` — the label folds it away and opens it again,
// remembered in this browser.
// `load` goes through the '@client/customs' alias, never a relative path: web/vite.config.js reads the
// client config and Vite bundles its config, so a relative import breaks `vite build`.
//
// Read through the namespace with the name as a key (as translator/frontend/store.js reads TRANSLATIONS):
// a fork whose client.config.js has no FIELD_VIEWS keeps building, and the build does not warn.
import { lazy } from 'react';
import * as clientConfig from '@client/config/client.config';

const SEAM = 'FIELD_VIEWS';
const RAW = clientConfig[SEAM];
const LIST = Array.isArray(RAW) ? RAW.filter((v) => v && v.field && typeof v.load === 'function') : [];
const norm = (s) => String(s || '').trim().toLowerCase();
const views = new Map();

function entryFor(field) {
  if (!LIST.length || !field) return null;
  return LIST.find((x) => x.field === field.key || norm(x.field) === norm(field.name)) || null;
}

// the fork's view of this screen field (a lazy component), or null
export function fieldViewFor(field) {
  const v = entryFor(field);
  if (!v) return null;
  if (!views.has(v)) views.set(v, lazy(v.load));
  return views.get(v);
}

// what the view says about its field's place on the screen: { label, collapsible }, or null
export function fieldViewOptions(field) {
  const v = entryFor(field);
  if (!v) return null;
  const label = typeof v.label === 'string' && v.label.trim() ? v.label.trim() : null;
  return { label, collapsible: v.collapsible === true };
}
export const hasFieldViews = () => LIST.length > 0;
